import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { VisualisationService } from 'services/visualisation.service';

import { CommonUtilsService, JSONObject, MQTT_KEYS, MQTT_TOPICS, mqttSyncSubscriptions, RootState, Sector, useMqttSubscription, UUID } from '@umanforge/common';

import { EventManager } from 'interfaces/eventManager.interface';
import { SECTOR_SETTING_KEY } from 'constants/sectorSetting.constants';


export const useVisualisationEventManager = () => {

  const dispatch = useDispatch();

  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );

  //-------------- useState ---------------\\
  const [ interval, setInterval ] = useState<number>( 0 );

  const [ sector, setSector ] = useState<Sector | null>( null );
  const [ eventManagers, setEventManagers ] = useState<EventManager[]>( [] );
  const [ selectedEventManager, setSelectedEventManager ] = useState<EventManager | null>( null );


  //-------------- useEffect --------------\\
  useEffect( () => {
    updateDatasFromSectors();
  }, [] );


  // Permet la rotation des tableaux du secteur tout les X temps
  // ---------------------------------->
  useEffect( () => {
    if ( eventManagers.length > 1 && interval > 0 ) {
      const timeout = setTimeout( () => {
        updateDatasFromSectors();
      }, interval );

      return () => {
        return clearTimeout( timeout );
      };
    }
  }, [ selectedEventManager?.id ] );

  useEffect( () => {
    if ( eventManagers.length > 0 ) {
      let nextIndex = eventManagers.findIndex( eventManager => { return eventManager.id === selectedEventManager?.id; } ) + 1;
      if ( nextIndex >= eventManagers.length ) {
        nextIndex = 0;
      }
      updateEventManager( eventManagers[ nextIndex ] );
    }
  }, [ eventManagers ] );
  // ---------------------------------->


  //-------------- Functions --------------\\
  const updateDatasFromSectors = async () => {
    const updatedSector = await VisualisationService.getSector( visualisationToken, dispatch );
    const updatedEventManagers = await VisualisationService.getEventManagers( visualisationToken, dispatch );
    const sortedEventManagers = CommonUtilsService.sortByKey( updatedEventManagers, 'position', 'asc' );

    const setting = await VisualisationService.getSectorSetting( SECTOR_SETTING_KEY.SWITCH_TIME, visualisationToken, dispatch );
    const newIntervalInterval = Number( setting.value );


    setSector( updatedSector );
    if ( updatedSector && ( sector?.id != updatedSector.id ) ) {
      const oldValues = [];
      if ( sector ) {
        oldValues.push( sector );
      }
      mqttSyncSubscriptions( oldValues, [ updatedSector ], MQTT_TOPICS.umSector );
    }

    setEventManagers( sortedEventManagers );
    if ( newIntervalInterval != interval ) {
      setInterval( sortedEventManagers.length > 1 ? newIntervalInterval : 0 );
    }
  };

  const refreshSector = async () => {
    const updatedSector = await VisualisationService.getSector( visualisationToken, dispatch );
    setSector( updatedSector );
  };

  const updateEventManager = async ( newEventManager: EventManager | undefined, isRequest: boolean = false ) => {
    let updatedEventManager: EventManager | null = null;
    try {
      if ( newEventManager ) {
        if ( isRequest ) {
          updatedEventManager = await VisualisationService.getEventManager( newEventManager.id, visualisationToken, dispatch );
        } else {
          updatedEventManager = newEventManager;
        }

        const oldValues = [];
        if ( selectedEventManager ) {
          oldValues.push( selectedEventManager );
        }
        mqttSyncSubscriptions( oldValues, [ updatedEventManager ], MQTT_TOPICS.fmEventManager );
      }
    } catch ( error ) {
      console.error( 'Failed to fetch event manager data:', error );
    } finally {
      setSelectedEventManager( updatedEventManager );
    }
  };


  //---------------- Mqtt -----------------\\
  useMqttSubscription( ( msg: JSONObject, category: string | null ) => {
    const payload = msg.payload as { sectorId?: UUID, eventManagerId?: UUID };

    // Met à jour la partie du nom du secteur
    if (
      category === MQTT_KEYS.UM_SECTOR &&
      sector?.id != null && payload.sectorId != null &&
      sector.id === payload.sectorId
    ) {
      refreshSector();
    }

    // Met à jour la partie du tableaux, themes, participants et graphique
    if (
      category === MQTT_KEYS.FM_EVENT_MANAGER &&
      selectedEventManager?.id != null && payload.eventManagerId != null &&
      selectedEventManager.id === payload.eventManagerId
    ) {
      updateEventManager( selectedEventManager, true );
    }

  } );


  return { selectedEventManager, sector, interval };
};